import { motion } from "framer-motion";
import { Reveal, MaskLineInView } from "./Reveal";
import { IMAGES } from "@/lib/apiClient";
import { Radio, Waves, Users2 } from "lucide-react";

const engines = [
  {
    n: "I",
    icon: Radio,
    title: "The Living Water Digital Mission",
    tag: "Witnessing & Recruitment",
    scripture: "John 7:37–39",
    body: "A daily stream of Christ-centered messages — one minute of Scripture, one clear truth, one invitation to Jesus — reaching the thirsty, isolated and confused across every screen.",
    points: ["One Minute of Living Water", "The W-A-T-E-R content formula", "Seven daily content pillars"],
    color: "#00E5FF",
  },
  {
    n: "II",
    icon: Waves,
    title: "The Acts 2:38 Salvation Pathway",
    tag: "Gospel Response & Soul Care",
    scripture: "Acts 2:37–42",
    body: "Every seeking soul receives a clear, compassionate, biblically-organized path — from a first conversation to repentance, baptism, and the gift of the Holy Spirit. The church witnesses; the Spirit convicts; you respond freely.",
    points: ["Seven-stage salvation journey", "From Thirsty to Transformed study", "The 11:11 Prayer & Discernment Line"],
    color: "#D4AF37",
  },
  {
    n: "III",
    icon: Users2,
    title: "The Acts 2:42 Covenant Family",
    tag: "Belonging & Discipleship",
    scripture: "Acts 2:42–47",
    body: "No one walks alone. First-time guests become supported, grounded, serving members through the 72–7–30–90 care system, the Acts 2 home tables, and an eleven-point member-care net.",
    points: ["The nine-fruit culture covenant", "Living Water Community Table", "Member rescue protocol"],
    color: "#9D4EDD",
  },
];

export default function Engines() {
  return (
    <section id="engines" className="relative py-28 md:py-40 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.water} alt="" className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-[#05030A]/85" />
      </div>

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal><span className="section-label">02 — The Living Water Ecosystem</span></Reveal>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.02] mt-5 text-white">
            <MaskLineInView>Three engines.</MaskLineInView>
            <MaskLineInView delay={0.1}><span className="italic text-gold">One flowing river.</span></MaskLineInView>
          </h2>
          <Reveal delay={0.2}>
            <p className="text-lg text-[#B4ADC1] font-light mt-6 leading-relaxed">
              The Fountain must become a river. The building is the fountainhead, the homes become streams, and the members become carriers. Three low-cost, self-sustaining ministry engines — each supporting the other two.
            </p>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-16">
          {engines.map((e, i) => {
            const Icon = e.icon;
            return (
              <motion.div
                key={e.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                className="group relative glass rounded-3xl p-8 overflow-hidden hover:-translate-y-2 transition-transform duration-500"
                style={{ boxShadow: `0 10px 50px ${e.color}10` }}
                data-testid={`engine-${e.n}`}
              >
                <div className="absolute top-0 left-0 right-0 h-px opacity-60" style={{ background: `linear-gradient(90deg, transparent, ${e.color}, transparent)` }} />
                <div className="flex items-center justify-between">
                  <span className="font-display text-6xl font-light" style={{ color: e.color }}>{e.n}</span>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center border" style={{ borderColor: `${e.color}44`, color: e.color, background: `${e.color}0f` }}>
                    <Icon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                </div>

                <p className="font-mono text-[0.62rem] uppercase tracking-[0.25em] mt-8" style={{ color: e.color }}>{e.tag}</p>
                <h3 className="font-display text-2xl text-white mt-2 leading-tight">{e.title}</h3>
                <p className="text-sm text-[#B4ADC1] font-light mt-4 leading-relaxed">{e.body}</p>

                <div className="mt-6 space-y-2.5">
                  {e.points.map((p) => (
                    <div key={p} className="flex items-center gap-3 text-sm text-[#C9C3D4]">
                      <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: e.color }} />
                      {p}
                    </div>
                  ))}
                </div>

                <p className="mt-7 pt-5 border-t border-white/8 font-display italic text-lg" style={{ color: e.color }}>{e.scripture}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
